import React, { useState } from "react"
import { Clock } from "lucide-react"

const FALLBACK_ICON =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iOCIgZmlsbD0iIzM3NDE1MSIvPgo8cGF0aCBkPSJNMTMgMjZIMTNWMTNIMFYxMEgxM1Y4SDhWNEgxNlY4SDE0VjEwSDE0VjEzSDI2VjI2SDE0VjI4SDEwVjI2SDEzWiIgZmlsbD0iIzljYTNhZiIvPgo8L3N2Zz4="

const PAGE_SIZE = 5

// `last_used_at` is UTC with no trailing "Z" (RECENT_APPS_API.md gotcha #1) -
// JS parses a bare date-time as local time, so append it ourselves.
const formatLastUsed = (isoNoZ) => {
  if (!isoNoZ) return null
  try {
    return new Date(`${isoNoZ}Z`).toLocaleString()
  } catch {
    return null
  }
}

/**
 * "Recently Searched" panel - same clock-label + card-grid + pager visual
 * language as AI CMO's "Recently Opened" list, sourced instead from the
 * `/socials/*\/recent-apps/` endpoints (RECENT_APPS_API.md) so any screen
 * that reads that shape (`app_id`, `name`, `image_url`, `game_url`,
 * `last_used_at`) can drop this in as-is.
 */
const RecentApps = ({ apps = [], loading = false, onSelect, title = "Recently Searched" }) => {
  const [page, setPage] = useState(1)

  if (!loading && apps.length === 0) return null

  const totalPages = Math.max(1, Math.ceil(apps.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const visible = apps.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  return (
    <div className="mt-4 mb-2">
      <div className="d-flex align-items-center gap-2 mb-3">
        <Clock size={14} color="#9ca3af" />
        <span
          style={{
            fontSize: "13px",
            color: "#9ca3af",
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {title}
        </span>
      </div>

      <div className="rq-recent-apps-grid">
        {loading
          ? Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <div key={i} className="rq-recent-apps-card rq-recent-apps-skeleton">
                <div className="rq-recent-apps-skeleton-icon" />
                <div className="rq-recent-apps-skeleton-line" style={{ animationDelay: `${i * 0.1}s` }} />
              </div>
            ))
          : visible.map((app) => (
              <div
                key={app.app_id}
                className="rq-recent-apps-card"
                title={formatLastUsed(app.last_used_at) ? `Last searched ${formatLastUsed(app.last_used_at)}` : app.name}
                onClick={() => onSelect(app)}
              >
                <img
                  src={app.image_url || FALLBACK_ICON}
                  alt={app.name}
                  onError={(e) => { e.target.src = FALLBACK_ICON }}
                />
                <span>{app.name}</span>
              </div>
            ))}
      </div>

      {!loading && apps.length > PAGE_SIZE && (
        <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
          <button
            type="button"
            className="rq-recent-apps-pager-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span style={{ color: "#9ca3af", fontSize: "12px", fontWeight: 500 }}>
            {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            className="rq-recent-apps-pager-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      <style>{`
        .rq-recent-apps-grid {
          display: grid;
          gap: 12px;
          grid-template-columns: repeat(5, minmax(0, 1fr));
        }
        @media (max-width: 1199px) { .rq-recent-apps-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
        @media (max-width: 991px) { .rq-recent-apps-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
        @media (max-width: 767px) { .rq-recent-apps-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 479px) { .rq-recent-apps-grid { grid-template-columns: repeat(1, minmax(0, 1fr)); } }
        .rq-recent-apps-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          height: 68px;
          min-width: 0;
          background: rgba(55,65,81,0.5);
          border: 1px solid #374151;
          border-radius: 10px;
          cursor: pointer;
          overflow: hidden;
          transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
        }
        .rq-recent-apps-card:hover {
          background: rgba(55,65,81,0.8);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          border-color: #F6255A;
        }
        .rq-recent-apps-card img {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          object-fit: cover;
          flex-shrink: 0;
        }
        .rq-recent-apps-card span {
          color: #fff;
          font-size: 14px;
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .rq-recent-apps-skeleton { cursor: default; }
        .rq-recent-apps-skeleton-icon {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          background: rgba(156,163,175,0.2);
          flex-shrink: 0;
          animation: rq-recent-apps-pulse 1.5s ease-in-out infinite;
        }
        .rq-recent-apps-skeleton-line {
          flex: 1;
          height: 14px;
          width: 80%;
          background: rgba(156,163,175,0.2);
          border-radius: 4px;
          animation: rq-recent-apps-pulse 1.5s ease-in-out infinite;
        }
        @keyframes rq-recent-apps-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .rq-recent-apps-pager-btn {
          padding: 6px 12px;
          min-width: 70px;
          background: rgba(246,37,90,0.8);
          border: 1px solid rgba(246,37,90,0.3);
          border-radius: 6px;
          color: #fff;
          cursor: pointer;
          font-size: 12px;
          transition: background 0.2s ease;
        }
        .rq-recent-apps-pager-btn:hover:not(:disabled) { background: rgba(246,37,90,1); }
        .rq-recent-apps-pager-btn:disabled {
          background: rgba(246,37,90,0.2);
          color: rgba(246,37,90,0.5);
          cursor: not-allowed;
        }
      `}</style>
    </div>
  )
}

export default RecentApps
